import { useMemo } from 'react';

import type { IztExamQuestion } from '../../api/types';
import { ChevronLeftIcon, ChevronRightIcon } from '../../layout/icons';
import { sanitizeExamHtml } from '../quiz/examHtml';
import { formatScore, sheetTally, studentSheet, type AnswerVerdict, type ExamAttempt, type SheetRow } from './examResults';

const VERDICT_LABEL: Record<AnswerVerdict, string> = { correct: 'Đúng', wrong: 'Sai', blank: 'Bỏ trống' };

/** IZ exam html, sanitized, formulas and figures intact. */
export function ExamHtml({ html, className }: { html: string; className?: string }) {
  const clean = useMemo(() => sanitizeExamHtml(html), [html]);
  return <span className={className ? 'exam-html ' + className : 'exam-html'} dangerouslySetInnerHTML={{ __html: clean }} />;
}

function SheetQuestion({ row }: { row: SheetRow }) {
  return (
    <li className="exam-sheet__row">
      <div className="exam-sheet__head">
        <span className="exam-sheet__no">Câu {row.index + 1}</span>
        <span className={`exam-sheet__verdict exam-sheet__verdict--${row.verdict}`}>{VERDICT_LABEL[row.verdict]}</span>
      </div>
      <div className="exam-sheet__stem">{row.stemHtml ? <ExamHtml html={row.stemHtml} /> : row.hint || 'Câu hỏi không có nội dung xem trước.'}</div>
      {row.options.length > 0 ? (
        <ul className="exam-sheet__options">
          {row.options.map((option) => {
            const tone = option.correct ? ' exam-sheet__option--correct' : option.picked ? ' exam-sheet__option--wrong' : '';
            return (
              <li key={option.key} className={'exam-sheet__option' + tone}>
                <span className="exam-sheet__label">{option.label}</span>
                <ExamHtml html={option.html} />
                {option.picked ? <span className={option.correct ? 'exam-sheet__mark exam-sheet__mark--correct' : 'exam-sheet__mark exam-sheet__mark--wrong'}>Em chọn</span>
                  : option.correct ? <span className="exam-sheet__mark exam-sheet__mark--correct">Đáp án đúng</span> : null}
              </li>
            );
          })}
        </ul>
      ) : (
        <div className="exam-sheet__typed">
          <div><strong>Em trả lời:</strong>{row.typed || '(bỏ trống)'}</div>
          {row.correctText ? <div><strong>Đáp án đúng:</strong><ExamHtml html={row.correctText} /></div> : null}
        </div>
      )}
    </li>
  );
}

export interface ExamStudentSheetProps {
  /** Submitted students in Xếp hạng order; only they are navigable. */
  order: ExamAttempt[];
  index: number;
  names: Record<string, string>;
  questions: IztExamQuestion[];
  onStep: (index: number) => void;
  onBack: () => void;
}

// One student's graded attempt, question by question, with prev/next across the class
export function ExamStudentSheet({ order, index, names, questions, onStep, onBack }: ExamStudentSheetProps) {
  const attempt = order[index];
  const rows = useMemo(() => (attempt ? studentSheet(attempt, questions) : []), [attempt, questions]);
  if (!attempt) return null;
  const tally = sheetTally(rows);
  const meta = [attempt.score !== null ? `${formatScore(attempt.score)} điểm` : '', `${tally.correct} đúng · ${tally.wrong} sai · ${tally.blank} bỏ trống`].filter(Boolean).join(' · ');
  return (
    <div className="exam-sheet">
      <button type="button" className="hw-panel__refresh exam-sheet__back" onClick={onBack}>Quay lại đề</button>
      <div className="exam-sheet__nav" role="group" aria-label="Bài làm của học sinh">
        <button type="button" className="exam-sheet__step" aria-label="Học sinh trước" disabled={index <= 0} onClick={() => onStep(index - 1)}><ChevronLeftIcon size={22} /></button>
        <div className="exam-sheet__who" aria-live="polite">
          <strong title={names[attempt.code] || attempt.name}>{names[attempt.code] || attempt.name}</strong>
          <span>Em {index + 1}/{order.length} · {meta}</span>
        </div>
        <button type="button" className="exam-sheet__step" aria-label="Học sinh tiếp theo" disabled={index >= order.length - 1} onClick={() => onStep(index + 1)}><ChevronRightIcon size={22} /></button>
      </div>
      {rows.length === 0 ? <p className="quiz-results__empty">Bài làm này không có câu nào.</p> : <ol className="exam-sheet__list">{rows.map((row) => <SheetQuestion key={row.id} row={row} />)}</ol>}
    </div>
  );
}
